import { type MouseEventData } from '../external-types';
import { fabric } from 'fabric';
import { type IPoint } from 'fabric/fabric-impl';
import type Shape from '../objects/Shape';
import type Canvas from '../objects/Canvas';
import tools from '../tools';
import { type Rect } from '../types';
import { svgToURL } from '../utils/svg';

const pointerSvg =
  svgToURL(`<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g filter="url(#filter0_d_13602_156)">
  <path d="M18.9986 5.99086L18.9985 5.99081L18.9928 5.99786L13.9446 12.2014C13.7205 12.4656 13.3909 12.6183 13.0393 12.6183H8.37781C8.35323 12.6183 8.3321 12.6038 8.32298 12.5838C8.31161 12.5589 8.31505 12.5319 8.33304 12.5103L8.33305 12.5103L8.3348 12.5081L16.0031 3.20261C16.379 2.75771 16.9343 2.5 17.5174 2.5C18.6123 2.5 19.4993 3.38702 19.4993 4.48191V4.62252C19.4993 5.12325 19.3231 5.60657 18.9986 5.99086Z" fill="black" stroke="white"/>
  <path d="M2.58118 19.479C4.0408 17.6859 7.30191 13.6964 8.25799 12.6549H12.1164C12.0532 13.9167 11.7466 15.0026 11.194 15.9064C10.5717 16.9241 9.61713 17.7447 8.26982 18.3266C6.42183 19.1246 3.99166 19.3919 2.58118 19.479Z" fill="black" stroke="white"/>
  </g>
  <defs>
  <filter id="filter0_d_13602_156" x="2.04395" y="2" width="19.9551" height="19.983" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
  <feFlood flood-opacity="0" result="BackgroundImageFix"/>
  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
  <feOffset dx="1" dy="1"/>
  <feGaussianBlur stdDeviation="0.5"/>
  <feComposite in2="hardAlpha" operator="out"/>
  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_13602_156"/>
  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_13602_156" result="shape"/>
  </filter>
  </defs>
  </svg>
 `);

const pointerUrl = `url('${pointerSvg}') 0 16, crosshair`;

// Brush for slicing an image. Draws a rectangle to show the slice.
class ImageSlicingBrush extends fabric.BaseBrush {
  startPoint: IPoint;
  currentPoint: IPoint;

  constructor(
    private readonly canvas: Canvas,
    private readonly imageShape: Shape
  ) {
    super();
    this.canvas.setDrawingCursor(pointerUrl);
  }

  onMouseDown(pointer: IPoint, eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return;
    }

    this.startPoint = pointer;
    this.canvas.controller.actionSetStylusMenu(null);
  }

  onMouseMove(pointer: IPoint, eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return;
    }

    this.currentPoint = pointer;

    this.canvas.clearContext(this.canvas.contextTop);
    this._render();
  }

  onMouseUp(eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return true;
    }

    this.canvas.isDrawingMode = false;
    this.canvas._isCurrentlyDrawing = false;

    // Perform the slice.
    const rect = this._sliceRect();
    if (rect) {
      void tools.ToolSliceImage.commit(this.imageShape, rect);
    } else {
      // reselect the image shape if the user clicks without dragging
      this.canvas.setActiveObject(this.imageShape);
      tools.ToolSliceImage.action({
        controller: this.canvas.controller,
        selection: this.imageShape,
      });
    }

    this.canvas.controller.actionShowObjectToolbar();
    this.canvas.requestRenderAll();

    return false;
  }

  _sliceRect(): Rect | undefined {
    // User must drag in order for both points in the slice to be created. Early
    // out if they do not do this
    if (!this.startPoint || !this.currentPoint) return;

    const right =
      this.imageShape.left + this.imageShape.width * this.imageShape.scaleX;
    const bottom =
      this.imageShape.top + this.imageShape.height * this.imageShape.scaleY;
    const minX = Math.max(
      Math.min(this.startPoint.x, this.currentPoint.x, right),
      this.imageShape.left
    );
    const minY = Math.max(
      Math.min(this.startPoint.y, this.currentPoint.y, bottom),
      this.imageShape.top
    );
    const maxX = Math.min(
      Math.max(this.startPoint.x, this.currentPoint.x, this.imageShape.left),
      this.imageShape.left + this.imageShape.width * this.imageShape.scaleX
    );
    const maxY = Math.min(
      Math.max(this.startPoint.y, this.currentPoint.y, this.imageShape.top),
      this.imageShape.top + this.imageShape.height * this.imageShape.scaleY
    );

    return {
      left: minX,
      top: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  _render() {
    if (!this.startPoint || !this.currentPoint) return;

    const ctx = this.canvas.contextTop;
    const rect = this._sliceRect();

    if (!rect) return;

    this._saveAndTransform(ctx);
    if (rect.width >= 1 && rect.height >= 1) {
      // Draw a semi-transparent overlay over the rest of the canvas.
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      const coords = this.canvas.vptCoords;
      if (coords) {
        ctx.fillRect(
          coords.tl.x,
          coords.tl.y,
          coords.br.x - coords.tl.x,
          coords.br.y - coords.tl.y
        );
      }
      ctx.clearRect(rect.left, rect.top, rect.width, rect.height);
    }

    ctx.lineWidth = 1 / this.canvas.getZoom();
    ctx.strokeStyle = this.canvas.selectionBorderColor;
    ctx.strokeRect(rect.left, rect.top, rect.width, rect.height);
    ctx.restore();
  }
}

export default ImageSlicingBrush;
